import axios from 'axios';
import BranchesService from '@/api/services/branches.service';
import DeliveryZonesService from '@/api/services/delivery-zones.service';
import PrintStationsService from '@/api/services/print-stations.service';
import RestaurantsService from '@/api/services/restaurants.service';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { PermissionType } from '@/enums/PermissionType';
import type { IBranch } from '@/interfaces/IBranch';
import type { ICategory } from '@/interfaces/ICategory';
import type { IDeliveryZone } from '@/interfaces/IDeliveryZone';
import type { IHourRange } from '@/interfaces/IHourRange';
import type { IItem } from '@/interfaces/IItem';
import { MemberRole } from '@/interfaces/IMember';
import type { IMember } from '@/interfaces/IMember';
import type { IPrintStation } from '@/interfaces/IPrintStation';
import { clampText } from '../../cards/card-text';
import type { IHostToolDefinition } from '../../gpthelp-host-contract';
import { capList, decimalOf, omitNullish } from '../sanitize';
import { getOptionalNumber } from '../tool-args';
import { accessibleBranches } from '../tool-gate';
import {
  opensBranchesPage,
  STORE_SETTINGS_SECTIONS,
  storeSettingsSections,
} from '../store-settings-sections';
import type { TStoreSettingsSection } from '../store-settings-sections';
import { throwNormalizedApiError } from '../tool-http';
import { HostToolError } from '../tool-types';
import type {
  ICartadigitalAdminHostToolDeps,
  IGpthelpTool,
  IGpthelpToolGate,
  IGpthelpToolScope,
} from '../tool-types';
import { buildMenuToolHelpers } from './menu-tools';

/** Delivery zones per result. */
const ZONES_MAX = 50;
/** Names per print-station list (categories, products) and per list of names without a station. */
const PRINT_NAMES_MAX = 100;
/** Branches per result. */
const BRANCHES_MAX = 100;
/** Team members per result. */
const MEMBERS_MAX = 100;
/** Characters of a member's description a result keeps. */
const MEMBER_DESCRIPTION_MAX = 120;

/** The hours page's day numbers: 0 = Sunday … 6 = Saturday. */
const WEEKDAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
/** The day number the hours page uses for holidays («Feriados»). */
const HOLIDAYS_DAY = 7;

/** An email address anywhere in a text. */
const EMAIL_IN_TEXT = /[^\s@]+@[^\s@]+\.[^\s@]+/;
/** A run of digits written like a phone number (spaces, dots, dashes, brackets between them). */
const DIGIT_RUNS_IN_TEXT = /\+?\d[\d\s().-]*\d/g;
/** Digits from which a run may be a phone number. */
const PHONE_MIN_DIGITS = 6;

const SECTION_FORBIDDEN =
  'This member cannot see this part of the settings. Tell the user; do not retry.';

/** What each section holds, as the description lists it (plan §8 R1). */
const SECTION_DOCS: Readonly<Record<TStoreSettingsSection, string>> = {
  ordering:
    "'ordering' = order types (table, take away, delivery), how orders arrive and the payment methods each order type accepts",
  hours: "'hours' = this branch's opening hours",
  delivery: "'delivery' = delivery cost, minimum and zones",
  printing:
    "'printing' = kitchen print stations and which categories and products print at each",
  branches: "'branches' = the branches you work in",
  team:
    "'team' = team members with their role, permissions and branches (no contact data)",
};

/** The sections that read another branch than the current one (`branchId`). */
const BRANCH_SECTIONS: readonly TStoreSettingsSection[] = [
  'ordering',
  'hours',
  'delivery',
];

/** The tool description, naming only `sections`. */
function settingsDescription(
  sections: readonly TStoreSettingsSection[]
): string {
  const listed = sections.map((section) => SECTION_DOCS[section]).join('; ');
  return `The store's configuration as the settings pages show it, one section per call (read-only): ${listed}. Read it before explaining how to change a setting so the steps match this store; the user makes changes on the settings pages.`;
}

/**
 * The tool's definition for `sections`: the description and the parameters
 * name only those (F10: nothing is offered and then refused). With every
 * section it is the plan's text verbatim.
 */
function settingsDefinition(
  sections: readonly TStoreSettingsSection[]
): IHostToolDefinition {
  const branchSections = sections.filter((section) =>
    BRANCH_SECTIONS.includes(section)
  );
  const listed = branchSections.join(', ');
  // Only the branch sections read another branch.
  const branchId = branchSections.length
    ? {
        branchId: {
          type: 'integer',
          description: `${listed}: another branch you work in (default: the current one).`,
        },
      }
    : {};
  return {
    name: 'store_getSettings',
    description: settingsDescription(sections),
    sideEffects: 'none',
    parameters: {
      type: 'object',
      additionalProperties: false,
      required: ['section'],
      properties: {
        section: { type: 'string', enum: [...sections] },
        ...branchId,
      },
    },
  };
}

/**
 * The branch a branch section reads: `branchId` when the member works in it,
 * else the current one. Checked for every section, so a branch the member
 * does not work in is always refused.
 */
function targetBranch(
  args: Record<string, unknown>,
  deps: ICartadigitalAdminHostToolDeps
): IBranch {
  const requested = getOptionalNumber(args, 'branchId');
  if (requested === null) {
    const current = deps.restaurantStore.selectedBranch;
    if (!current)
      throw new HostToolError('NO_SCOPE', 'No restaurant/branch selected');
    return current;
  }
  const branches = accessibleBranches(deps);
  const branch = branches.find((entry) => entry.id === requested);
  if (!branch) {
    throw new HostToolError(
      'INVALID_ARGS',
      'The member does not work in this branch.',
      { branchId: requested, allowed: branches.map((entry) => entry.id) }
    );
  }
  return branch;
}

/**
 * Reads one section's source. The API refuses a page's data with 401 (a
 * missing permission), 403 or 404: only this call fails.
 */
async function readSource<T>(
  section: TStoreSettingsSection,
  read: () => Promise<T>
): Promise<T> {
  try {
    return await read();
  } catch (error) {
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;
    if (status === 401 || status === 403 || status === 404) {
      throw new HostToolError('FORBIDDEN', SECTION_FORBIDDEN, {
        section,
        status,
      });
    }
    return throwNormalizedApiError(error);
  }
}

/** Trimmed text, or null when there is none. */
function textOf(value: string | null | undefined): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}

/** «09:00:00» (a MySQL time) as the hours page shows it: «09:00». */
function clockOf(value: string): string {
  const match = /^(\d{2}:\d{2})(:\d{2})?$/.exec(value);
  return match ? match[1] : value;
}

/** The key names of a numeric flag enum whose bits `mask` holds, in declaration order. */
function flagNames(
  flags: Record<string, string | number>,
  mask: number | null | undefined
): string[] {
  const bits = mask ?? 0;
  return Object.entries(flags)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
    .filter(([, bit]) => (bits & bit) === bit)
    .map(([name]) => name);
}

function branchRef(branch: IBranch) {
  return { id: branch.id, name: branch.name };
}

/**
 * 'ordering': the order types, how orders arrive and the payment methods of
 * each type (A = table, B = take away, C = delivery). `onlinePaymentsLinked`
 * says only whether online payments are linked, never with what.
 */
export function orderingSettingsOf(
  branch: IBranch,
  onlinePaymentsLinked: boolean
) {
  return omitNullish({
    branch: branchRef(branch),
    allowOrders: branch.allowOrders,
    anonymousOrders: branch.anonymousOrders,
    ordersMode: branch.ordersMode,
    tableOrders: branch.tableOrders,
    tableOrdersAskPaymentMethod: branch.tableOrdersAskPaymentMethod,
    takeAway: branch.takeAway,
    takeAwayOnlyQr: branch.takeAwayOnlyQr,
    delivery: branch.delivery,
    deliveryMin: decimalOf(branch.deliveryMin),
    requireFullAddress: branch.requireFullAddress,
    paymentMethods: {
      table: flagNames(PaymentMethod, branch.paymentMethodsA),
      takeAway: flagNames(PaymentMethod, branch.paymentMethodsB),
      delivery: flagNames(PaymentMethod, branch.paymentMethodsC),
    },
    onlinePaymentsLinked,
  });
}

/** A range's times as the hours page shows them. */
function clocksOf(range: IHourRange) {
  return { from: clockOf(range.from), to: clockOf(range.to) };
}

/** Monday first, like the hours page. */
function weekOrderOf(day: number): number {
  return (day + 6) % 7;
}

/**
 * 'hours': the branch's opening hours by day name, Monday first, and the
 * holiday hours apart. With «Mostrar horarios» off, or no hours at all, the
 * branch is always open (the panel and the diner app treat it so), whatever
 * ranges it still keeps.
 */
export function hoursSettingsOf(branch: IBranch, ranges: IHourRange[]) {
  const showHours = !!branch.displayHours;
  const byTime = (a: IHourRange, b: IHourRange) => a.from.localeCompare(b.from);
  const hours = ranges
    .filter((range) => range.day >= 0 && range.day < WEEKDAY_NAMES.length)
    .sort((a, b) => weekOrderOf(a.day) - weekOrderOf(b.day) || byTime(a, b))
    .map((range) => ({ day: WEEKDAY_NAMES[range.day], ...clocksOf(range) }));
  const holidays = ranges
    .filter((range) => range.day === HOLIDAYS_DAY)
    .sort(byTime)
    .map(clocksOf);
  if (!showHours || (!hours.length && !holidays.length)) {
    return omitNullish({
      branch: branchRef(branch),
      showHours,
      alwaysOpen: true,
    });
  }
  return omitNullish({
    branch: branchRef(branch),
    showHours,
    hours,
    holidays: holidays.length ? holidays : null,
  });
}

/** The settings page's «Costo de envío» options. */
export type TDeliveryCostMode =
  | 'free'
  | 'agreedWithCustomer'
  | 'fixed'
  | 'perZone';

/** Which «Costo de envío» option the branch uses, read like the settings page does. */
function deliveryCostModeOf(branch: IBranch): TDeliveryCostMode {
  if (branch.zonedFee) return 'perZone';
  const cost = decimalOf(branch.deliveryCost);
  if (cost === null) return 'agreedWithCustomer';
  return cost === 0 ? 'free' : 'fixed';
}

/**
 * 'delivery': whether the branch takes delivery orders, the fee, the minimum
 * order and, when delivery is limited to zones, the zones (names, fee when it
 * depends on the zone, on or off). Never their shape or coordinates.
 */
export function deliverySettingsOf(branch: IBranch, zones: IDeliveryZone[]) {
  const costMode = deliveryCostModeOf(branch);
  const listed = branch.zonedDelivery
    ? capList(
        zones.filter((zone) => !zone.deletedAt),
        ZONES_MAX
      )
    : null;
  return omitNullish({
    branch: branchRef(branch),
    // Delivery can be ordered only while the branch takes orders («Habilitar
    // pedidos», which hides the delivery settings when off) and delivery is
    // on (NewOrderModal `deliveryAvailable`). Off: the settings below stay as
    // saved, but nobody can order delivery.
    enabled: branch.allowOrders && branch.delivery,
    deliveryCostMode: costMode,
    deliveryCost: costMode === 'fixed' ? decimalOf(branch.deliveryCost) : null,
    deliveryMin: decimalOf(branch.deliveryMin),
    zonedDelivery: branch.zonedDelivery,
    zones:
      listed?.results.map((zone) =>
        omitNullish({
          name: zone.name,
          fee: costMode === 'perZone' ? decimalOf(zone.cost) : null,
          active: zone.enabled,
        })
      ) ?? null,
    truncated: listed?.truncated ?? null,
  });
}

/** Whether `printers` names a station that exists now. */
function printsAtSome(
  printers: number[] | null | undefined,
  stationIds: ReadonlySet<number>
): boolean {
  return (printers ?? []).some((id) => stationIds.has(id));
}

/**
 * 'printing': each station with the categories that print there and the
 * products whose own stations (they override their category's) include it;
 * the categories and products with their own stations that print nowhere.
 */
export function printingSettingsOf(
  stations: IPrintStation[],
  categories: ICategory[],
  items: IItem[]
) {
  const stationIds = new Set(stations.map((station) => station.stationId));
  // `comandaPrinters: null` = the product prints where its category does.
  const ownStations = items.filter((item) =>
    Array.isArray(item.comandaPrinters)
  );

  const rows = stations.map((station) => {
    const listed = capList(
      categories
        .filter((category) =>
          (category.comandaPrinters ?? []).includes(station.stationId)
        )
        .map((category) => category.name),
      PRINT_NAMES_MAX
    );
    const products = capList(
      ownStations
        .filter((item) =>
          (item.comandaPrinters ?? []).includes(station.stationId)
        )
        .map((item) => item.name),
      PRINT_NAMES_MAX
    );
    return omitNullish({
      id: station.stationId,
      name: station.name,
      categories: listed.results,
      products: products.results,
      truncated: listed.truncated || products.truncated || null,
    });
  });

  const categoriesWithout = capList(
    categories
      .filter((category) => !printsAtSome(category.comandaPrinters, stationIds))
      .map((category) => category.name),
    PRINT_NAMES_MAX
  );
  const productsWithout = capList(
    ownStations
      .filter((item) => !printsAtSome(item.comandaPrinters, stationIds))
      .map((item) => item.name),
    PRINT_NAMES_MAX
  );
  return omitNullish({
    stations: rows,
    categoriesWithoutStation: categoriesWithout.results,
    productsWithoutStation: productsWithout.results,
    truncated: categoriesWithout.truncated || productsWithout.truncated || null,
  });
}

/**
 * 'branches': the branches the member works in; their address and city only
 * for members who may open the «Sucursales» page. Never tokens, Wi-Fi
 * passwords, payment credentials, mail settings, phones or coordinates.
 */
export function branchesSettingsOf(
  branches: IBranch[],
  currentBranchId: number,
  withAddress: boolean
) {
  const listed = capList(branches, BRANCHES_MAX);
  return omitNullish({
    branches: listed.results.map((branch) =>
      omitNullish({
        id: branch.id,
        name: branch.name,
        active: branch.active,
        current: branch.id === currentBranchId,
        address: withAddress ? textOf(branch.address) : null,
        city: withAddress ? textOf(branch.city) : null,
      })
    ),
    truncated: listed.truncated ?? null,
  });
}

/** Whether a text written in the panel holds an email address or a phone number. */
function holdsContactData(text: string): boolean {
  if (EMAIL_IN_TEXT.test(text)) return true;
  return (text.match(DIGIT_RUNS_IN_TEXT) ?? []).some(
    (run) => run.replace(/\D/g, '').length >= PHONE_MIN_DIGITS
  );
}

/**
 * The member's description, the free note the team page shows under the
 * name (not a role): clamped, and left out when it holds contact data.
 */
function memberDescriptionOf(value: string | null | undefined): string | null {
  const text = textOf(value);
  if (!text || holdsContactData(text)) return null;
  return clampText(text, MEMBER_DESCRIPTION_MAX);
}

/** One member as the team page shows it: never contact data, user ids or tokens. */
function teamMemberOf(member: IMember) {
  // Owners hold every branch whatever their role says (the rule `hasRole` applies).
  const everyBranch = member.type === MemberRole.Owner || member.allBranches;
  return omitNullish({
    name: textOf(member.user?.name),
    role: member.type,
    description: memberDescriptionOf(member.description),
    // Owners and admins hold every permission.
    permissions:
      member.type === MemberRole.Custom
        ? flagNames(PermissionType, member.permissions)
        : null,
    allBranches: everyBranch,
    branches: everyBranch
      ? null
      : (member.branches ?? []).map((branch) => branch.name),
    allCajas: member.allCajas,
    hasPin: !!member.hasPin,
  });
}

/** 'team': the members with their role, permissions and branches. */
export function teamSettingsOf(members: IMember[]) {
  const listed = capList(members, MEMBERS_MAX);
  return omitNullish({
    members: listed.results.map(teamMemberOf),
    truncated: listed.truncated ?? null,
  });
}

/** Reads one allowed section from its page's source. */
async function readSection(
  section: TStoreSettingsSection,
  branch: IBranch,
  deps: ICartadigitalAdminHostToolDeps,
  gate: IGpthelpToolGate,
  scope: IGpthelpToolScope
) {
  switch (section) {
    case 'ordering': {
      // `mpData` never reaches the store's branches (the API does not
      // select it): the settings page reads it here, and so does this.
      const payments = await readSource(section, () =>
        new BranchesService().getPaymentsData(branch.id)
      );
      return orderingSettingsOf(branch, !!payments?.mpData);
    }
    case 'hours':
      return hoursSettingsOf(
        branch,
        await readSource(section, () =>
          new BranchesService().getHours(branch.id)
        )
      );
    case 'delivery':
      return deliverySettingsOf(
        branch,
        await readSource(section, () =>
          DeliveryZonesService.getByBranch(branch.id)
        )
      );
    case 'printing': {
      const { categories, items } = buildMenuToolHelpers(
        deps
      ).getMenuCatalogSnapshotOrThrow();
      const stations = await readSource(section, () =>
        PrintStationsService.getAll(scope.restaurantId)
      );
      return printingSettingsOf(stations, categories, items);
    }
    case 'branches':
      // Every branch's address is on the «Sucursales» page. The settings page
      // also shows the current branch's location when the store has a single
      // branch (RestaurantSettings.vue «Ubicación», `!multipleBranches`);
      // this section keeps to the «Sucursales» gate (plan §8 R1).
      return branchesSettingsOf(
        accessibleBranches(deps),
        scope.branchId,
        opensBranchesPage(gate)
      );
    case 'team':
      return teamSettingsOf(
        await readSource(section, () =>
          RestaurantsService.getMembers(scope.restaurantId)
        )
      );
    default: {
      const unknown: never = section;
      throw new HostToolError('INVALID_ARGS', 'Unknown section', {
        section: unknown,
      });
    }
  }
}

/** `store_getSettings` (plan §8 R1): the settings pages' data, read-only, one section per call. */
export const storeGetSettingsTool: IGpthelpTool = {
  definition: settingsDefinition(STORE_SETTINGS_SECTIONS),
  definitionFor: (gate) => settingsDefinition(storeSettingsSections(gate)),
  policy: 'read',
  isAvailable: (gate) => storeSettingsSections(gate).length > 0,
  async execute(args, { deps, gate, scope }) {
    const allowed = storeSettingsSections(gate);
    const raw = args.section;
    const value = typeof raw === 'string' ? raw.trim() : raw ?? null;
    const section = STORE_SETTINGS_SECTIONS.find((entry) => entry === value);
    // Missing or unknown: the answer names only the sections this member may read.
    if (!section) {
      throw new HostToolError('INVALID_ARGS', 'Missing/invalid "section"', {
        key: 'section',
        value,
        allowed,
      });
    }
    if (!allowed.includes(section)) {
      throw new HostToolError('FORBIDDEN', SECTION_FORBIDDEN, { section });
    }
    const branch = targetBranch(args, deps);
    return await readSection(section, branch, deps, gate, scope);
  },
};

/** The store settings reads (registered before the exports, plan F11). */
export const storeTools: IGpthelpTool[] = [storeGetSettingsTool];
